/**
 * API Client
 * 
 * Centralized HTTP client for communicating with the PratBot backend.
 * Handles authentication headers, error handling, and response parsing.
 * 
 * Usage:
 *   import { api } from '@/api/client'
 *   
 *   const channels = await api.get('/api/channels')
 *   await api.post('/api/channels', { name: 'mychannel' })
 */

const API_BASE_URL = import.meta.env.VITE_API_URL || ''

/**
 * Get the stored JWT token.
 */
function getToken(): string | null {
  return localStorage.getItem('pratbot_token')
}

/**
 * Set the JWT token.
 */
export function setToken(token: string): void {
  localStorage.setItem('pratbot_token', token)
}

/**
 * Clear the JWT token (logout).
 */
export function clearToken(): void {
  localStorage.removeItem('pratbot_token')
}

/**
 * Check if we have a token stored.
 */
export function hasToken(): boolean {
  return !!getToken()
}

/**
 * API error with status code and response data.
 */
export class ApiError extends Error {
  constructor(
    public status: number,
    public statusText: string,
    public data?: unknown,
  ) {
    super(`API Error: ${status} ${statusText}`)
    this.name = 'ApiError'
  }
}

/**
 * Pull a human-readable message out of a failed request.
 *
 * FastAPI puts the useful part in `detail`, which ApiError carries untyped in
 * `data`. Without this every toast and banner reads "API Error: 403 Forbidden"
 * and throws away the sentence explaining what was actually refused.
 *
 * Lives beside ApiError deliberately: it is a property of the error shape this
 * module throws, not of any one endpoint. It was written for the admin module
 * registry (R1) and stranded in adminModulesApi.ts until R15, during which
 * every other page rendered err.message straight to the user.
 */
export function apiErrorMessage(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    const data = err.data as { detail?: unknown } | undefined
    if (typeof data?.detail === 'string' && data.detail.trim()) {
      return data.detail
    }
  }
  return fallback
}

/**
 * Fired on `window` when any request is refused with 503 `database_unavailable`
 * (R24). The database banner listens and rechecks `/health` at once rather than
 * waiting for its next poll.
 */
export const DATABASE_UNAVAILABLE_EVENT = 'pratbot:database-unavailable'

/** What `/health` reports. 200 when the database answered, 503 otherwise. */
export interface HealthStatus {
  status: 'healthy' | 'degraded'
  database: 'ok' | 'unavailable' | 'not_initialised'
}

/**
 * Read `/health` (unauthenticated). Resolves to its body on 200 OR 503 -- both
 * are answers -- and to null when the server itself could not be reached, which
 * is a different problem from the database and not this check's to report.
 */
export async function fetchHealth(): Promise<HealthStatus | null> {
  try {
    const response = await fetch(`${API_BASE_URL}/health`, { cache: 'no-store' })
    if (response.status !== 200 && response.status !== 503) return null
    return (await response.json()) as HealthStatus
  } catch {
    return null
  }
}

/**
 * Build headers for API requests.
 */
function buildHeaders(customHeaders?: HeadersInit): Headers {
  const headers = new Headers(customHeaders)
  
  // Set content type if not already set
  if (!headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json')
  }
  
  // Add auth token if available
  const token = getToken()
  if (token) {
    headers.set('Authorization', `Bearer ${token}`)
  }
  
  return headers
}

/**
 * Make an API request.
 */
async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  customHeaders?: HeadersInit,
): Promise<T> {
  const url = `${API_BASE_URL}${path}`
  const headers = buildHeaders(customHeaders)
  
  const options: RequestInit = {
    method,
    headers,
  }
  
  if (body !== undefined) {
    options.body = JSON.stringify(body)
  }
  
  const response = await fetch(url, options)
  
  // Handle non-2xx responses
  if (!response.ok) {
    let errorData: unknown
    try {
      errorData = await response.json()
    } catch {
      // Response wasn't JSON
    }
    if (
      response.status === 503 &&
      (errorData as { code?: unknown } | undefined)?.code === 'database_unavailable'
    ) {
      window.dispatchEvent(new Event(DATABASE_UNAVAILABLE_EVENT))
    }
    throw new ApiError(response.status, response.statusText, errorData)
  }
  
  // Handle empty responses
  if (response.status === 204) {
    return undefined as T
  }
  
  // Parse JSON response
  return response.json()
}

/**
 * API client with convenience methods for each HTTP verb.
 */
export const api = {
  /**
   * GET request.
   */
  get<T>(path: string, headers?: HeadersInit): Promise<T> {
    return request<T>('GET', path, undefined, headers)
  },
  
  /**
   * POST request.
   */
  post<T>(path: string, body?: unknown, headers?: HeadersInit): Promise<T> {
    return request<T>('POST', path, body, headers)
  },
  
  /**
   * PUT request.
   */
  put<T>(path: string, body?: unknown, headers?: HeadersInit): Promise<T> {
    return request<T>('PUT', path, body, headers)
  },
  
  /**
   * PATCH request.
   */
  patch<T>(path: string, body?: unknown, headers?: HeadersInit): Promise<T> {
    return request<T>('PATCH', path, body, headers)
  },
  
  /**
   * DELETE request.
   */
  delete<T>(path: string, headers?: HeadersInit): Promise<T> {
    return request<T>('DELETE', path, undefined, headers)
  },
}

export default api
